import React, { useEffect, useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { AccentText, useDocumentMeta, useI18n } from "../i18n";
import Content0 from "../Home/Content0";
import BdfArchitecture from "../Home/diagrams/BdfArchitecture";
import { getPageData } from "../Home/pageData";
import KindTag from "../Home/KindTag";
import BDF_APPLIANCE_IMG from "../Home/storage-server.png";

export default function BdfPage() {
  const { t } = useI18n();
  const p = t.bdfPage;
  const home = useMemo(() => getPageData(t), [t]);

  useDocumentMeta({
    title: p.metaTitle,
    description: p.metaDescription,
    path: "/products/bdf",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="nfw-product-page">
      <header className="nfw-product-hero nfw-bdf-hero">
        <div className="nfw-wrap">
          <div className="nfw-bdf-hero-grid">
            <div>
              <div className="nfw-kind-row">
                <KindTag kind="product" label={t.kind.product} />
              </div>
              <p className="nfw-kicker">{p.eyebrow}</p>
              <h1>
                {p.title}
                <br />
                <em>{p.titleAccent}</em>
              </h1>
              <p className="nfw-product-hero-lead">{p.lead}</p>
              <div className="nfw-hero-actions">
                <a
                  className="nfw-btn nfw-btn-solid"
                  href="/downloads/bdf-datasheet.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {p.download}
                  <ArrowRight size={18} />
                </a>
                <a className="nfw-btn nfw-btn-ghost nfw-glass" href="/#contact">
                  {p.contact}
                </a>
              </div>
            </div>
            <figure className="nfw-bdf-hero-shot">
              <img src={BDF_APPLIANCE_IMG} alt={p.heroImgAlt} />
            </figure>
          </div>
          <div className="nfw-product-metrics">
            {p.metrics.map((item) => (
              <div className="nfw-metric nfw-glass" key={item.label}>
                <b>{item.value}</b>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </header>

      <section className="nfw-section">
        <div className="nfw-wrap nfw-product-prose">
          <p className="nfw-kicker">{p.overview.kicker}</p>
          <h2 className="nfw-h2">{p.overview.title}</h2>
          <p className="nfw-lead nfw-product-wide-lead">{p.overview.lead}</p>
          <div className="nfw-desc">
            <AccentText text={p.overview.body} />
          </div>
        </div>
      </section>

      <section className="nfw-section nfw-section-alt">
        <div className="nfw-wrap">
          <div className="nfw-section-head">
            <div>
              <p className="nfw-kicker">{p.family.kicker}</p>
              <h2 className="nfw-h2">{p.family.title}</h2>
            </div>
            <p className="nfw-lead">{p.family.subtitle}</p>
          </div>
          <div className="nfw-spec-table">
            <table>
              <thead>
                <tr>
                  {p.family.columns.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {p.family.rows.map((row) => (
                  <tr key={row[0]}>
                    {row.map((cell, i) => (
                      <td key={`${row[0]}-${i}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="nfw-product-note">
            {p.family.driveNote}
            <br />
            {p.family.note}
          </p>
        </div>
      </section>

      <section className="nfw-section">
        <div className="nfw-wrap">
          <div className="nfw-section-head">
            <div>
              <p className="nfw-kicker">{p.hardware.kicker}</p>
              <h2 className="nfw-h2">{p.hardware.title}</h2>
            </div>
            <p className="nfw-lead">{p.hardware.subtitle}</p>
          </div>
          <div className="nfw-points nfw-product-points">
            {p.hardware.points.map((item, i) => (
              <div className="nfw-point nfw-glass" key={item.title}>
                <b>{String(i + 1).padStart(2, "0")}</b>
                <div>
                  <strong>{item.title}</strong>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="nfw-section nfw-section-alt">
        <div className="nfw-wrap">
          <div className="nfw-split nfw-split-top">
            <div className="nfw-split-copy">
              <p className="nfw-kicker">{p.software.kicker}</p>
              <h2>{p.software.title}</h2>
              <p className="nfw-lead">{p.software.subtitle}</p>
              <div className="nfw-desc">
                <AccentText text={p.software.body} />
              </div>
            </div>
            <div>
              <div className="nfw-media nfw-glass">
                <BdfArchitecture title={t.bdf.imgAlt} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="nfw-section">
        <div className="nfw-wrap">
          <div className="nfw-section-head">
            <div>
              <p className="nfw-kicker">{p.perf.kicker}</p>
              <h2 className="nfw-h2">{p.perf.title}</h2>
            </div>
          </div>
          <div className="nfw-spec-table">
            <table>
              <thead>
                <tr>
                  {p.perf.columns.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {p.perf.rows.map((row) => (
                  <tr key={row[0]}>
                    {row.map((cell, i) => (
                      <td key={`${row[0]}-${i}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="nfw-product-insights">
            {p.perf.insights.map((item) => (
              <p key={item}>{item}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="nfw-section nfw-section-alt">
        <div className="nfw-wrap">
          <div className="nfw-section-head">
            <div>
              <p className="nfw-kicker">{p.adv.kicker}</p>
              <h2 className="nfw-h2">{p.adv.title}</h2>
            </div>
            <p className="nfw-lead">{p.adv.subtitle}</p>
          </div>
          <div className="nfw-spec-table">
            <table>
              <thead>
                <tr>
                  {p.adv.columns.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {p.adv.rows.map((row) => (
                  <tr key={row[0]}>
                    {row.map((cell, i) => (
                      <td key={`${row[0]}-${i}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <Content0 dataSource={home.bdfScenarios} />
    </main>
  );
}
